import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  SwipeableDrawer,
  IconButton,
  Avatar,
  Paper,
  useMediaQuery,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useBaseImageUrl from "../hooks/useBaseImageUrl";
import { getUserData } from "../redux/slices/user/thunk";

export default function UserQRCode({ open, onClose, user: propUser }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const baseUrl = useBaseImageUrl();

  const reduxUser = useSelector((state) => state.user?.user);
  const [loading, setLoading] = useState(false);
  const [fetchedUser, setFetchedUser] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasFetchedRef.current = false;
      return;
    }

    if (hasFetchedRef.current) return;

    const storedUser = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user")) : null;
    const currentUser = reduxUser || propUser || storedUser || {};
    const userId = currentUser?._id || currentUser?.id;

    if (!currentUser?.qrCode && userId) {
      hasFetchedRef.current = true;
      setLoading(true);
      dispatch(getUserData(userId))
        .unwrap()
        .then((res) => {
          const rawUser =
            res?.data?.data ||
            res?.data?.user ||
            res?.data ||
            res?.user ||
            res;

          if (rawUser && typeof rawUser === "object" && !Array.isArray(rawUser)) {
            const stored = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user")) : {};
            const updatedUser = { ...(stored || {}), ...(currentUser || {}), ...rawUser };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            setFetchedUser(updatedUser);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user data for QR code:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, propUser, reduxUser, dispatch]);

  const storedUser = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("user")) : null;
  const user = reduxUser || fetchedUser || propUser || storedUser || {};

  // Data to store inside QR
  const qrValue = user?.qrCode || "";

  // QR Content UI
  const QrContent = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        p: 3,
        bgcolor: "background.paper",
      }}
    >
      {/* Modal / Drawer Header */}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight={900}>
          {t("nav.qr")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ width: "100%", mb: 3 }} />

      {/* User Info */}
      <Avatar
        src={user?.image && user.image !== "null" ? `${baseUrl}${user.image}` : undefined}
        sx={{ width: 80, height: 80, mb: 2, border: `3px solid ${theme.palette.primary.main}` }}
      >
        {user?.name?.[0]}
      </Avatar>
      <Typography variant="h6" fontWeight={800}>
        {user?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {user?.mobile}
      </Typography>

      {/* QR Code Container */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: "#fff", // White background for optimal QR scanning
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: isMobile ? 240 : 220,
          minHeight: isMobile ? 240 : 220,
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : (
          <QRCodeSVG
            value={qrValue || "no-data"}
            size={isMobile ? 240 : 220}
            level="H"
            includeMargin={false}
            imageSettings={{
              src: "/Logo.png",
              height: 50,
              width: 50,
              excavate: true,
            }}
          />
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        {isRtl ? "إعرض الكود للموظف لمسحه ضوئياً" : "Show this code to the staff"}
      </Typography>
    </Box>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 25,
            borderTopRightRadius: 25,
            maxHeight: "80vh",
          },
        }}
      >
        {/* Drag handle for mobile */}
        <Box
          sx={{
            width: 40,
            height: 5,
            bgcolor: "grey.400",
            borderRadius: 10,
            mx: "auto",
            mt: 1.5,
          }}
        />
        <QrContent />
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, p: 1 },
      }}
    >
      <DialogContent>
        <QrContent />
      </DialogContent>
    </Dialog>
  );
}
